import React, { useCallback, useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Button, Form, message, Tabs, Tooltip } from 'antd';

import { Giide, IState, SocialUrls } from '../../graphql/types';
import useGiideSettings from '../../hooks/useGiideSettings';
import useMutation from '../../hooks/useMutation';
import {
  CardDetailsInput,
  UpdateGiideInput,
  UpdateGiideMutation,
} from '../../graphql/api';
import { clearEditorData, updateGiideSettings } from '../../actions';
import {
  decodeSocialUrls,
  encodeSocialUrls,
  SettingsTabKeys as TabKeys,
  settingsTabArray as tabsArray,
  SettingsTabText as TabText,
  handleImageInEditor,
  getGiideSettingsTooltipMessage,
} from '../utils';

import ImageSettings from './ImageSettings';
import DetailsSettings from './DetailsSettings';

import styles from './GiideSettings.module.css';
import AvatarEditor from 'react-avatar-editor';
import { ImageEditorValidation } from '../../types';

const { TabPane } = Tabs;
interface IProps {
  giide: Giide;
  tenantId: string;
  userId: string;
  closeModal: () => void;
}

const GiideSettings: React.FC<IProps> = ({
  giide,
  tenantId,
  userId,
  closeModal,
}) => {
  const {
    giideData,
    updateInput,
    updateColor,
    updateShade,
    updateThumbnail,
    updateSpeakerInput,
    updateSpeakerUrlInput,
    updateSpeakerName,
    avatar,
    handleAvatarUpload,
    removeAvatarHandler,
    uploadAvatarProgress,
    updateThumbnailData,
    uploadThumbnail,
    removeThumbnail,
    selectCustomThumbnail,
    updateGiideMutation,
    // invites,
    // addInvite,
    // removeInvite,
    // saveInvites,
    setGiideData,
    updateGiideTitleName,
  } = useGiideSettings(giide, tenantId, userId);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKeys>(tabsArray[0]);
  const [editorRef, setEditorRef] = useState<AvatarEditor | null>(null);
  const useTemplate = useSelector<IState, boolean>(
    state =>
      state.useTemplate ??
      !!(
        !state.giide?.cardDetails?.useCustomThumbnail &&
        state.giide?.cardDetails?.thumbnailImg
      )
  );
  const imageEditorValidation = useSelector<IState, ImageEditorValidation>(
    state => state.imageEditorValidation ?? { failed: false }
  );
  const imageMimeTypeLoaded = useSelector<IState, string>(
    state => state.imageMimeTypeLoaded ?? ''
  );

  const [detailsForm] = Form.useForm();
  const [imageForm] = Form.useForm();

  const dispatch = useDispatch();
  const { callMutation } = useMutation<UpdateGiideInput, UpdateGiideMutation>(
    updateGiideMutation
  );

  const saveSettings = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      try {
        await detailsForm.validateFields();
      } catch (e) {
        message.error('Please fill in all required fields in Details tab.');
        return;
      }

      try {
        await imageForm.validateFields();
      } catch (e) {
        message.error('Please fill in all required fields in Image tab.');
        return;
      }

      try {
        // if (!giideData.speaker.headshotPath) {
        //   message.error('Please upload author profile image.');
        //   return;
        // }
        setSaving(true);

        const uploadData: CardDetailsInput = await handleImageInEditor(
          editorRef,
          imageMimeTypeLoaded,
          dispatch,
          uploadThumbnail
        );

        const encodedSocialUrls = encodeSocialUrls(
          giideData.speaker.socialUrls as SocialUrls
        );
        const giideDataInput = {
          ...giideData,
          speaker: {
            ...giideData.speaker,
            socialUrls: encodedSocialUrls,
          },
        };

        if (uploadData) {
          giideDataInput.cardDetails = uploadData;
        } else {
          giideDataInput.cardDetails = {
            ...giideData.cardDetails,
            thumbnailCustomName: useTemplate
              ? ''
              : giideData.cardDetails.thumbnailCustomName,
            thumbnailCustomPath: useTemplate
              ? ''
              : giideData.cardDetails.thumbnailCustomPath,
            useCustomThumbnail: !useTemplate,
          };
        }

        if (uploadData || uploadData === undefined) {
          // const invitesSend = saveInvites();
          // if (!invitesSend) {
          //   throw new Error('An error occured while saving permissions');
          // }

          const result = await callMutation(giideDataInput);

          if (result.data.updateGiide) {
            giideDataInput.speaker.socialUrls = decodeSocialUrls(
              giideData.speaker.socialUrls as SocialUrls
            );
            batch(() => {
              dispatch(updateGiideSettings(giideDataInput));
              dispatch(clearEditorData());
            });
            closeModal();
          } else {
            throw new Error('Error occurred while saving settings.');
          }
        }
      } catch (e) {
        message.error('Error occurred while saving settings.');
        setSaving(false);
      } finally {
        setSaving(false);
      }
    },
    [
      giideData,
      dispatch,
      callMutation,
      closeModal,
      detailsForm,
      imageForm,
      // saveInvites,
      editorRef,
      uploadThumbnail,
      useTemplate,
      imageMimeTypeLoaded,
    ]
  );

  const handleClose = useCallback(() => {
    dispatch(clearEditorData());
    closeModal();
  }, [dispatch, closeModal]);

  const tooltipMessage = useMemo<string | undefined>(() => {
    return getGiideSettingsTooltipMessage(
      activeTab,
      useTemplate,
      giideData,
      imageEditorValidation
    );
  }, [giideData, activeTab, useTemplate, imageEditorValidation]);

  const buttons = {
    right: (
      <div>
        <Button
          className={styles.cancelBtn}
          disabled={saving}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Tooltip title={tooltipMessage}>
          <Button
            className={styles.saveBtn}
            onClick={saveSettings}
            loading={saving}
            disabled={loading || saving || !!tooltipMessage}
          >
            Save
          </Button>
        </Tooltip>
      </div>
    ),
  };

  return (
    <div className="giideSettingsWrapper giideSettingsContent">
      <div className={styles.giideSettingsContent}>
        <Tabs
          activeKey={activeTab}
          type="card"
          tabBarExtraContent={buttons}
          onChange={key => setActiveTab(TabKeys[key])}
        >
          {tabsArray.map(tabKey => {
            if (tabKey === TabKeys.details) {
              return (
                <TabPane tab={TabText[TabKeys.details]} key={TabKeys.details}>
                  <DetailsSettings
                    giideData={giideData}
                    updateSpeakerInput={updateSpeakerInput}
                    updateSpeakerUrlInput={updateSpeakerUrlInput}
                    updateSpeakerName={updateSpeakerName}
                    detailsForm={detailsForm}
                    progress={uploadAvatarProgress}
                    avatar={avatar}
                    handleUpload={handleAvatarUpload}
                    removeAvatar={removeAvatarHandler}
                    updateGiideInput={updateInput}
                    saving={saving}
                    updateGiideTitleName={updateGiideTitleName}
                    // dummy values, replace if this settings modal gets used again
                    toggleNoIndex={() => {}}
                    noIndexLoading={false}
                  />
                </TabPane>
              );
            } else if (tabKey === TabKeys.image) {
              return (
                <TabPane tab={TabText[TabKeys.image]} key={TabKeys.image}>
                  <ImageSettings
                    giideData={giideData}
                    updateInput={updateInput}
                    updateColor={updateColor}
                    updateShade={updateShade}
                    updateThumbnail={updateThumbnail}
                    imageForm={imageForm}
                    uploadThumbnail={uploadThumbnail}
                    removeThumbnail={removeThumbnail}
                    selectCustomThumbnail={selectCustomThumbnail}
                    updateThumbnailData={updateThumbnailData}
                    editorRef={editorRef}
                    setEditorRef={setEditorRef}
                    setLoading={setLoading}
                    loading={loading}
                    saving={saving}
                    setGiideData={setGiideData}
                  />
                </TabPane>
              );
            }
            // else if (tabKey === TabKeys.audioTones) {
            //   return (
            //     <TabPane
            //       tab={TabText[TabKeys.audioTones]}
            //       key={TabKeys.audioTones}
            //     >
            //       <AudioTonesSettings
            //         useGiideTones={giideData.useGiideTones}
            //         updateInput={updateInput}
            //       />
            //     </TabPane>
            //   );
            // }
            // else if (tabKey === TabKeys.permissions) {
            //   return (
            //     <TabPane
            //       tab={TabText[TabKeys.permissions]}
            //       key={TabKeys.permissions}
            //     >
            //       <PermissionsSettings
            //         giideData={giideData}
            //         updateInput={updateInput}
            //         invites={invites}
            //         addInvite={addInvite}
            //         removeInvite={removeInvite}
            //       />
            //     </TabPane>
            //   );
            // }
            return null;
          })}
        </Tabs>
      </div>
    </div>
  );
};

export default GiideSettings;
